// Global
import React, { useEffect, useRef } from 'react';
import { PureHeaderPrimaryLinkBar, PureHeaderSectionLink } from './types';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import styles from './Header.module.scss';
import clsx from 'clsx';
import { HeaderDesktop } from './HeaderDesktop/HeaderDesktop';
import { HeaderMobile } from './HeaderMobile/HeaderMobile';
import { useIsTablet } from 'lib/hooks/useIsTablet';
import useClickOutside from 'lib/hooks/useClickOutside';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { LinkContext } from 'components/helpers/Atoms/Cta/CtaLink';
import useDictionary from 'lib/hooks/useDictionary';
interface HeaderMegaMenuContainerProps {
  primaryLinkBar: PureHeaderPrimaryLinkBar;
  isOpen: boolean;
  onToggle: () => void;
  openSubMenu: boolean;
  setOpenSubMenu: (sectionLink?: PureHeaderSectionLink) => void;
  selectedSectionLink: PureHeaderSectionLink | null;
  firstInteractiveElementRef?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>;
  primaryLinkBarIndex: number;
  isMobileMegaNavOpen: boolean;
  linkContext: LinkContext;
}

export const HeaderMegaMenuContainer = (props: HeaderMegaMenuContainerProps) => {
  const {
    primaryLinkBar,
    isOpen,
    onToggle,
    openSubMenu,
    setOpenSubMenu,
    selectedSectionLink,
    firstInteractiveElementRef,
    primaryLinkBarIndex,
    isMobileMegaNavOpen,
    linkContext,
  } = props;
  const { getDictionaryValue } = useDictionary();
  const isTablet = useIsTablet();

  // Refs for focus management
  const itemRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Only use click outside behavior in desktop mode, not tablet/mobile
  useClickOutside(itemRef, isOpen && !isTablet, () => onToggle());

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKeyPress);
      document.addEventListener('focusin', handleFocusChange);
    }

    return () => {
      // Cleanup Function
      document.removeEventListener('keydown', handleEscapeKeyPress);
      document.removeEventListener('focusin', handleFocusChange);
    };
  });

  const handleButtonClick = () => {
    // Toggle the accordion state for both desktop and mobile/tablet
    onToggle();
  };

  // Closes the mega menu and returns focus to the header menu button,
  // matching the Escape key behaviour for accessibility.
  const handleCloseWithFocus = () => {
    const buttonElement = buttonRef.current as HTMLElement | null;
    buttonElement?.setAttribute('aria-expanded', 'false');
    buttonElement?.focus();
    onToggle();
  };

  function handleEscapeKeyPress(e: KeyboardEvent) {
    // Early return if not escape key
    if (e.key !== 'Escape') {
      return;
    }

    // If focus is inside the nav, focus button before closing
    const navElement = itemRef.current as HTMLElement | null;
    const buttonElement = buttonRef.current as HTMLElement | null;
    if (navElement?.contains(e.target as Node)) {
      buttonElement?.setAttribute('aria-expanded', 'false');
      buttonElement?.focus();
    }

    onToggle();
  }

  function handleFocusChange(e: FocusEvent) {
    // Return early if focus has left the document (aka dev tools, etc)
    if (isTablet) {
      return;
    }
    if (!document.hasFocus()) {
      return;
    }

    const navElement = itemRef.current as HTMLElement | null;
    if (navElement && !navElement.contains(e.target as Node)) {
      onToggle();
    }
  }

  const setOpenSubMenuEvent = (sectionLink?: PureHeaderSectionLink) => {
    if (!isOpen) return;
    setOpenSubMenu(sectionLink);
  };

  const primaryLinkLabel = primaryLinkBar.primaryLinkLabel?.trim();
  const megaNavListId = `meganav-list-${primaryLinkLabel || `item-${primaryLinkBarIndex}`}`;
  const menuButtonLabel = `${primaryLinkLabel} ${getDictionaryValue('Navigation.Header.MenuButton') || 'menu'}`;
  const opensInNewTabText = getDictionaryValue('Common.OpensInNewTabs') || '(Opens in a new tab)';
  const externalLinkLabel =
    primaryLinkBar.primaryLinkTarget === '_blank'
      ? `${primaryLinkLabel} ${opensInNewTabText}`
      : primaryLinkLabel;

  return (
    <li ref={itemRef}>
      {primaryLinkBar.linkType !== 'internal' ? (
        <CtaLink
          className={clsx(styles.headerMenuBarLink, isOpen && styles.isOpenLink)}
          url={primaryLinkBar.primaryLinkUrl}
          text={primaryLinkBar.primaryLinkLabel}
          ctaStyle="none"
          ref={
            (!isTablet
              ? buttonRef
              : (firstInteractiveElementRef as React.RefObject<HTMLAnchorElement>)) as React.Ref<HTMLAnchorElement>
          }
          aria-hidden={!isMobileMegaNavOpen && isTablet}
          tabIndex={!isMobileMegaNavOpen && isTablet ? -1 : 0}
          target={primaryLinkBar.primaryLinkTarget}
          linkContext={linkContext}
          aria-label={externalLinkLabel}
        />
      ) : (
        <>
          {primaryLinkBar?.isNavDropDownMenuLink &&
          (primaryLinkBar?.primaryLinks?.length ?? 0) === 0 ? (
            <CtaLink
              text={primaryLinkBar?.primaryLinkLabel}
              url={primaryLinkBar?.primaryLinkUrl}
              ctaStyle="none"
              className={clsx(styles.headerMenuBarLink, isOpen && styles.isOpenLink)}
              target={primaryLinkBar.primaryLinkTarget}
              linkContext={linkContext}
            />
          ) : (
            <button
              type="button"
              ref={
                !isTablet
                  ? buttonRef
                  : (firstInteractiveElementRef as React.RefObject<HTMLButtonElement>)
              }
              className={clsx(styles.headerMenuBarLink, isOpen && styles.isOpenLink)}
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-controls={megaNavListId}
              onClick={handleButtonClick}
              aria-label={menuButtonLabel}
              aria-hidden={!isMobileMegaNavOpen && isTablet ? 'true' : 'false'}
              tabIndex={!isMobileMegaNavOpen && isTablet ? -1 : 0}
            >
              <span className={styles.headerMenuBarLinkText}>
                {primaryLinkBar.primaryLinkLabel}
              </span>
              {(primaryLinkBar.isNavDropDownMenuLink &&
                primaryLinkBar.primaryLinks &&
                primaryLinkBar.primaryLinks.length > 0) ||
              !primaryLinkBar.isNavDropDownMenuLink ? (
                <>
                  {isTablet ? (
                    <SvgIcon icon={isOpen ? 'minus' : 'plus'} size="sm" />
                  ) : (
                    <SvgIcon icon={'chevron-up'} size="xs" />
                  )}
                </>
              ) : null}
            </button>
          )}
        </>
      )}
      <div
        className={clsx(
          styles.megaMenuContainerWrapper,
          isOpen && styles.isOpen,
          primaryLinkBar.isNavDropDownMenuLink && styles.isNavDropDownMenuLink
        )}
        id={megaNavListId}
      >
        {!isTablet && isOpen ? (
          <div className={styles.headerDesktop}>
            <HeaderDesktop
              primaryLinkBar={primaryLinkBar}
              isOpen={isOpen}
              setIsOpen={handleCloseWithFocus}
              primaryLinkBarIndex={primaryLinkBarIndex}
              linkContext={linkContext}
            />
          </div>
        ) : (
          isMobileMegaNavOpen && (
            <div className={styles.headerMobile}>
              <HeaderMobile
                primaryLinkBar={primaryLinkBar}
                isOpen={isOpen}
                setIsOpen={onToggle}
                openSubMenu={openSubMenu}
                setOpenSubMenu={setOpenSubMenuEvent}
                selectedSectionLink={selectedSectionLink}
                primaryLinkBarIndex={primaryLinkBarIndex}
                linkContext={linkContext}
              />
            </div>
          )
        )}
      </div>
    </li>
  );
};
